import { gsap } from 'gsap';

/********************
* DOMContentLoadedイベントハンドラ
*********************/
document.addEventListener('DOMContentLoaded', () => {

/********************
* HTMLElementの取得
*********************/
const imageContainer = document.querySelector('#image-container') as HTMLDivElement;
const contentContainer = document.querySelector('#content-container') as HTMLDivElement;
const prevButton = document.querySelector('#prev-button') as HTMLButtonElement;
const nextButton = document.querySelector('#next-button') as HTMLButtonElement;
const wrapper = document.querySelector('.js-circular') as HTMLDivElement;

const images = [...imageContainer.querySelectorAll('img')] as HTMLImageElement[]; 
const contentItems = [...contentContainer.querySelectorAll('.circular__contentItem')] as HTMLDivElement[];

const totalItems = images.length;
let activeIndex = 0;
let autoplayInterval: number;

// ページ読み込み完了後に初期表示を確実に行うフラグ
let initialLoadComplete = false;

/********************
* ユーティリティ関数
*********************/
// 画面サイズに基づいてギャップを計算する関数
const calculateGap = (width: number): number => {
  const minWidth = 768;
  const maxWidth = 1456;
  const minGap = 40;
  const maxGap = 86;

  if (width <= minWidth) return minGap;
  if (width >= maxWidth) return Math.max(minGap, maxGap + 0.06 * (width - maxWidth));

  return minGap + (maxGap - minGap) * ((width - minWidth) / (maxWidth - minWidth));
};

/********************
* テキスト処理関数
*********************/
const wrapWordsInSpan = (element: HTMLElement | null) => {
  if (!element) return;
  const text = element.textContent || '';
  
  // 単純に文字ごとに分割
  const characters = text.split('');
  const result = characters.map(char => {
    // スペースはそのまま返す
    if (char === ' ' || char === '　') return char;
    return `<span class="word">${char}</span>`;
  }).join('');
  
  element.innerHTML = result;
};

/********************
* 画像更新関数
*********************/
const updateImages = () => {
  // コンテナの幅を取得してギャップを計算
  const containerWidth = imageContainer.offsetWidth;
  const gap = calculateGap(containerWidth);
  const maxStickUp = gap * 0.8; // 最大高さの調整（元コードと同様）

  images.forEach((image, index) => {
    const offset = (index - activeIndex + totalItems) % totalItems;
    const zIndex = totalItems - Math.abs(offset);
    // すべての画像のopacityを1に統一（元のコードに合わせる）
    const opacity = 1;
    const scale = index === activeIndex ? 1 : 0.85;

    let translateX, translateY, rotateY;
    
    if (offset === 0) {
      // アクティブな画像
      translateX = '0%';
      translateY = '0%';
      rotateY = 0;
    } else if (offset === 1 || offset === -2) {
      // 右側の画像
      translateX = '20%';
      translateY = `-${maxStickUp / image.offsetHeight * 100}%`; // 画像の高さに対する割合で調整
      rotateY = -15;
    } else {
      // 左側の画像
      translateX = '-20%';
      translateY = `-${maxStickUp / image.offsetHeight * 100}%`;
      rotateY = 15;
    }

    // GSAPを使用してアニメーションを設定 - durationを短くしてスピードアップ
    gsap.to(image, {
      zIndex: zIndex,
      opacity: opacity,
      scale: scale,
      x: translateX,
      y: translateY,
      rotateY: rotateY,
      duration: 0.6, // 0.8から0.6に短縮してよりスピーディーに
      ease: 'power2.out', // power3.outからpower2.outに変更してより滑らかに
    });
  });
};

/********************
* コンテンツ更新関数
*********************/
const updateContents = () => {
  // 初期ロード時は処理をスキップ（初期化関数で設定済み）
  if (!initialLoadComplete) return;

  // すべてのコンテンツアイテムを非表示に
  contentItems.forEach((item, index) => {
    if (index !== activeIndex) {
      gsap.set(item, { opacity: 0, display: 'none' });
    }
  });

  // アクティブなコンテンツアイテムの準備
  const activeItem = contentItems[activeIndex];
  
  // 単語要素を取得し、最初は不透明度0に設定
  const words = activeItem.querySelectorAll('.word');
  gsap.set(words, { opacity: 0, y: 0 });
  
  // アクティブアイテムを表示する（ただし単語は不可視のまま）
  gsap.set(activeItem, { display: 'block', opacity: 1, y: 0 });
  
  // 単語のアニメーションを開始
  gsap.to(words, {
    opacity: 1,
    y: 0,
    stagger: 0.01,
    duration: 0.2,
    ease: 'power1.out'
  });
};

// ページロード完了時に追加チェックを行う
window.addEventListener('load', () => {
  // 画像とテキストが表示されていることを確認
  const activeItem = contentItems[activeIndex];
  const words = activeItem.querySelectorAll('.word');
  
  // もし単語が不透明でない場合は強制的に表示
  words.forEach(word => {
    if (getComputedStyle(word as HTMLElement).opacity !== '1') {
      gsap.set(word, { opacity: 1, y: 0 });
    }
  });
});

/********************
* カルーセル更新のメイン関数
*********************/
const updateCarousel = (direction: number) => {
  // 前のアクティブインデックスを保存
  const oldIndex = activeIndex;
  
  // 新しいインデックスを計算
  activeIndex = (activeIndex + direction + totalItems) % totalItems;
  
  // インデックスが変わった場合のみアニメーションを実行
  if (oldIndex !== activeIndex || direction === 0) {
    updateImages();
    updateContents();
  }
};

/********************
* 初期化関数
*********************/
const initialize = () => {
  // テキストのワード分割
  contentItems.forEach(item => {
    const title = item.querySelector('.circular__title') as HTMLHeadingElement | null;
    const text = item.querySelector('.circular__text') as HTMLParagraphElement | null;

    wrapWordsInSpan(title);
    wrapWordsInSpan(text);
    
    // 初期状態で非アクティブなコンテンツを非表示に
    if (parseInt(item.dataset.index || '0') !== activeIndex) {
      gsap.set(item, { opacity: 0, display: 'none' });
    } else {
      // アクティブなアイテムは表示
      gsap.set(item, { opacity: 1, display: 'block' });
      
      // アクティブアイテムの単語は、リロード直後ですぐに表示する
      const words = item.querySelectorAll('.word');
      gsap.set(words, { opacity: 1, y: 0 });
    }
  });

  // 初期状態のセットアップ
  updateImages();
  
  // 自動再生の設定
  startAutoplay();

  gsap.to(wrapper, { opacity: 1, duration: 2, ease: 'power2.out' });
  
  // 初期化完了を設定
  initialLoadComplete = true;
};

/********************
* 自動再生関数
*********************/
const startAutoplay = () => {
  // 既存の自動再生をクリア
  if (autoplayInterval) {
    clearInterval(autoplayInterval);
  }
  
  // 新しい自動再生を設定
  autoplayInterval = window.setInterval(() => updateCarousel(1), 5000);
};

const stopAutoplay = () => {
  if (autoplayInterval) {
    clearInterval(autoplayInterval);
    autoplayInterval = 0;
  }
};

/********************
* イベントリスナーの設定
*********************/
prevButton.addEventListener('click', () => {
  updateCarousel(-1);
  stopAutoplay();
});

nextButton.addEventListener('click', () => {
  updateCarousel(1);
  stopAutoplay();
});

// ウィンドウのリサイズに対応
window.addEventListener('resize', () => {
  updateCarousel(0); // 現在のインデックスを維持しつつ更新
});

// 初期化関数を呼び出す
initialize();

// オプション: タッチとスワイプのサポート追加
let touchStartX = 0;
imageContainer.addEventListener('touchstart', (e) => {
  touchStartX = e.touches[0].clientX;
  stopAutoplay();
}, { passive: true });

imageContainer.addEventListener('touchend', (e) => {
  const touchEndX = e.changedTouches[0].clientX;
  const diff = touchEndX - touchStartX;
  
  if (Math.abs(diff) > 50) { // スワイプの最小距離
    updateCarousel(diff > 0 ? -1 : 1);
  }
}, { passive: true });

});
